import {FC, useEffect, useState} from 'react';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import {
  DataGrid,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridValueGetterParams
} from '@mui/x-data-grid';
import { FaEye } from 'react-icons/fa';
import { IconButton } from '@mui/material';
// ..custom
import { ISubscriptionTableData } from '../../views/admin/subscriptions/Subscription.view';
import SimpleSpinner from '../loaders/spinners/SimpleSpinner';


type ISubscriptionAdminTableProps = {
  list: ISubscriptionTableData[],
  onRowSelect: (item: string) => void
};

type ISubscriptionTableProps = Omit<ISubscriptionTableData, 'id'> & {'id': number, 'subscriptionId': string };


type IMuiTableProps = {
  list: ISubscriptionTableProps[];
  onRowSelect: (item: string) => void
};

const ContainerStyled = styled.section`
    width: 100%;
    min-height: 300px;
    max-height: 800px;
    table {
        width: 100%
    }
`;

const CustomToolbar = () => {
  return (
    <GridToolbarContainer sx={{ justifyContent: 'flex-end', backgroundColor: '#f5ebe0', '& .MuiButton-root, & .MuiButtonBase-root': { color: 'black' } }}>
      <GridToolbarColumnsButton placeholder="" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} onResize={undefined} onResizeCapture={undefined} />
      <GridToolbarFilterButton placeholder="" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} onResize={undefined} onResizeCapture={undefined} />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
};

// Mui Table component
const MuiTableAdmin: FC<IMuiTableProps> = ({ list, onRowSelect }) => {

  // material columns
  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      headerClassName: 'custom-header',
    },
    {
      field: 'name',
      headerName: 'Name',
      sortable: true,
      width: 250,
      headerClassName: 'custom-header',
    },
    {
      field: 'country',
      headerName: 'Country',
      sortable: true,
      width: 250,
      headerClassName: 'custom-header',
    },
    {
      field: 'email',
      headerName: 'Email',
      sortable: true,
      width: 250,
      headerClassName: 'custom-header',
    },
    {
      field: 'contact',
      headerName: 'Contact No',
      sortable: true,
      width: 200,
      headerClassName: 'custom-header',
    },
    {
      field: 'event_title',
      headerName: 'Event Name',
      sortable: true,
      width: 500,
      headerClassName: 'custom-header',
    },
    {
      field: 'reg_type',
      headerName: 'Registration Type',
      sortable: true,
      width: 200,
      headerClassName: 'custom-header',
    },
    {
      field: 'payment_type',
      headerName: 'Payment Type',
      sortable: true,
      width: 200,
      headerClassName: 'custom-header',
    },
    {
      field: 'amount',
      headerName: 'Amount Paid',
      type: 'string',
      width: 200,
      sortable: true,
      headerClassName: 'custom-header',
      valueGetter: (data: GridValueGetterParams) => {
        return data.row.amount ? data.row.amount : '-';
      },
    },
    {
      field: 'status',
      headerName: 'Subscription Status',
      type: 'string',
      width: 200,
      sortable: true,
      headerClassName: 'custom-header',
      valueGetter: (data: GridValueGetterParams) => {
        return data.row.status ? data.row.status : '-';
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 200,
      headerClassName: 'custom-header',
      renderCell: (params) => {
        return (
          <div className="flex justify-around">
            <IconButton onClick={() => onRowSelect((params.row as ISubscriptionTableProps).subscriptionId)}>
              <FaEye />
            </IconButton>
          </div>
        );
      }
    }
  ];

  return (<Box sx={{ height: 600, width: '100%' }}>
    <DataGrid
      style={{ fontSize: 15 }}
      rows={list}
      columns={columns}
      components={{ Toolbar: CustomToolbar }}
      initialState={{
        sorting: {
          sortModel: [{ field: 'id', sort: 'desc' }],
        },
      }}
      sx={{
        '.custom-header': {
          backgroundColor: '#e3d5ca',
          fontWeight: 600,
          color: 'black',
        },
      }}
    />
  </Box>
  );
};


const SubscriptionAdminMuiTable: FC<ISubscriptionAdminTableProps> = ({list, onRowSelect}) => {
  // states
  const [title, setTitle] = useState<string>('');
  const [subTitle, setSubTitle] = useState<string>('Please wait...');
  const [subTableList, setSubTableList] = useState<ISubscriptionTableProps[]>([]);


  // lifecycle
    useEffect(()=> {
        if (list && list.length > 0) {
            const tableList: ISubscriptionTableProps[] = list.map((item: ISubscriptionTableData, idx: number) => ({ ...item, id: idx + 1, subscriptionId: item.id }));
            setTitle('Subscriptions List Detail');
            setSubTitle('List of user subscription...');
            setSubTableList(tableList);
        } else if (list && list.length === 0) {
            setTitle('No Subscriptions');
            setSubTitle('There are currently no subscriptions...');
        }
    },[list]);



  return (<ContainerStyled
        className="mt-2 relative bg-gray-100 dark:bg-gray-900 rounded-lg
                     flex flex-col justify-start items-start shadow-lg
                    p-4 overflow-hidden overflow-scroll">
        <div role="status"></div>
        <h2 className="text-gray-700 dark:text-teal-400 text-2xl font-medium mb-2">{title}</h2>
        <p className="text-gray-700 dark:text-gray-300 dartext-base mb-4">
            {subTitle}
        </p>
        <SimpleSpinner display={`inline-block`} loading={!list} title={'Loading subscriptions...'}/>
        {Boolean(list) && (list).length > 0 && <MuiTableAdmin list={subTableList} onRowSelect={onRowSelect}/>}
    </ContainerStyled>
  );
};

export default SubscriptionAdminMuiTable;
